export const getFailedChecks = (checks) => {
  if (!checks || typeof checks !== 'object') return [];

  const failedChecks = [];
  const checkDescriptions = {
    no_content_encoding: {
      label: 'No Content Encoding',
      severity: 'warning',
      description: 'Page content is not compressed'
    },
    high_loading_time: {
      label: 'High Loading Time',
      severity: 'warning',
      description: 'Page takes too long to load'
    },
    is_redirect: {
      label: 'Redirect Detected',
      severity: 'info',
      description: 'Page redirects to another URL'
    },
    is_4xx_code: {
      label: '4xx Error',
      severity: 'critical',
      description: 'Client error status code'
    },
    is_5xx_code: {
      label: '5xx Error',
      severity: 'critical',
      description: 'Server error status code'
    },
    is_broken: {
      label: 'Broken Page',
      severity: 'critical',
      description: 'Page is not accessible'
    },
    is_http: {
      label: 'Not Using HTTPS',
      severity: 'warning',
      description: 'Page is not served over HTTPS'
    },
    high_waiting_time: {
      label: 'High Waiting Time',
      severity: 'warning',
      description: 'Server response time is high'
    },
    has_micromarkup_errors: {
      label: 'Schema Markup Errors',
      severity: 'warning',
      description: 'Structured data has errors'
    },
    no_doctype: {
      label: 'Missing Doctype',
      severity: 'warning',
      description: 'HTML doctype declaration missing'
    },
    no_encoding_meta_tag: {
      label: 'Missing Encoding Meta Tag',
      severity: 'warning',
      description: 'Character encoding not specified'
    },
    no_h1_tag: {
      label: 'Missing H1 Tag',
      severity: 'warning',
      description: 'No H1 heading found on page'
    },
    https_to_http_links: {
      label: 'Mixed Content',
      severity: 'warning',
      description: 'HTTPS page links to HTTP resources'
    },
    size_greater_than_3mb: {
      label: 'Page Size Too Large',
      severity: 'warning',
      description: 'Page size exceeds 3MB'
    },
    has_meta_refresh_redirect: {
      label: 'Meta Refresh Redirect',
      severity: 'warning',
      description: 'Page uses meta refresh for redirect'
    },
    has_render_blocking_resources: {
      label: 'Render Blocking Resources',
      severity: 'warning',
      description: 'Resources block page rendering'
    },
    low_content_rate: {
      label: 'Low Content Rate',
      severity: 'info',
      description: 'Low text-to-HTML ratio'
    },
    high_content_rate: {
      label: 'High Content Rate',
      severity: 'info',
      description: 'Very high text-to-HTML ratio'
    },
    low_character_count: {
      label: 'Low Character Count',
      severity: 'info',
      description: 'Page has very few characters'
    },
    high_character_count: {
      label: 'High Character Count',
      severity: 'info',
      description: 'Page has many characters'
    },
    low_readability_rate: {
      label: 'Low Readability',
      severity: 'info',
      description: 'Content is difficult to read'
    },
    irrelevant_description: {
      label: 'Irrelevant Description',
      severity: 'warning',
      description: 'Meta description may not match content'
    },
    irrelevant_title: {
      label: 'Irrelevant Title',
      severity: 'warning',
      description: 'Page title may not match content'
    },
    irrelevant_meta_keywords: {
      label: 'Irrelevant Meta Keywords',
      severity: 'info',
      description: 'Meta keywords may not match content'
    },
    title_too_long: {
      label: 'Title Too Long',
      severity: 'warning',
      description: 'Page title exceeds recommended length'
    },
    title_too_short: {
      label: 'Title Too Short',
      severity: 'warning',
      description: 'Page title is too short'
    },
    deprecated_html_tags: {
      label: 'Deprecated HTML Tags',
      severity: 'warning',
      description: 'Page uses deprecated HTML elements'
    },
    duplicate_meta_tags: {
      label: 'Duplicate Meta Tags',
      severity: 'warning',
      description: 'Multiple identical meta tags found'
    },
    duplicate_title_tag: {
      label: 'Duplicate Title',
      severity: 'warning',
      description: 'Same title used on multiple pages'
    },
    no_image_alt: {
      label: 'Missing Image Alt Text',
      severity: 'warning',
      description: 'Images without alt attributes found'
    },
    no_image_title: {
      label: 'Missing Image Titles',
      severity: 'info',
      description: 'Images without title attributes found'
    },
    no_description: {
      label: 'Missing Meta Description',
      severity: 'warning',
      description: 'No meta description found'
    },
    no_title: {
      label: 'Missing Page Title',
      severity: 'critical',
      description: 'No page title found'
    },
    no_favicon: {
      label: 'Missing Favicon',
      severity: 'info',
      description: 'No favicon detected'
    },
    flash: {
      label: 'Flash Content',
      severity: 'warning',
      description: 'Page uses Flash (deprecated)'
    },
    frame: {
      label: 'Frame/IFrame Content',
      severity: 'info',
      description: 'Page uses frames'
    },
    lorem_ipsum: {
      label: 'Lorem Ipsum Detected',
      severity: 'warning',
      description: 'Placeholder text found on page'
    },
    seo_friendly_url: {
      label: 'Non-SEO Friendly URL',
      severity: 'info',
      description: 'URL structure could be improved'
    },
    recursive_canonical: {
      label: 'Recursive Canonical',
      severity: 'warning',
      description: 'Canonical URL points to itself incorrectly'
    },
    canonical_chain: {
      label: 'Canonical Chain',
      severity: 'warning',
      description: 'Multiple canonical redirects detected'
    },
    canonical_to_redirect: {
      label: 'Canonical to Redirect',
      severity: 'warning',
      description: 'Canonical URL redirects'
    },
    canonical_to_broken: {
      label: 'Canonical to Broken Page',
      severity: 'critical',
      description: 'Canonical URL points to broken page'
    },
    has_links_to_redirects: {
      label: 'Links to Redirects',
      severity: 'info',
      description: 'Page contains links that redirect'
    },
    is_orphan_page: {
      label: 'Orphan Page',
      severity: 'warning',
      description: 'Page has no internal links pointing to it'
    },
    redirect_chain: {
      label: 'Redirect Chain',
      severity: 'warning',
      description: 'Multiple redirects detected'
    }
  };

  Object.entries(checks).forEach(([key, value]) => {
    // Only include checks that are TRUE (indicating an issue)
    if (value === true && checkDescriptions[key]) {
      failedChecks.push({
        key,
        ...checkDescriptions[key]
      });
    }
  });

  return failedChecks;
};

export const getPassedChecks = (checks) => {
  if (!checks || typeof checks !== 'object') return [];

  const passedChecks = [];
  const checkDescriptions = {
    is_https: { label: 'Using HTTPS', description: 'Page is served securely' },
    canonical: {
      label: 'Canonical URL Set',
      description: 'Canonical URL is properly configured'
    },
    has_html_doctype: {
      label: 'HTML Doctype Present',
      description: 'Proper HTML doctype declared'
    },
    seo_friendly_url_characters_check: {
      label: 'SEO-Friendly URL Characters',
      description: 'URL uses valid characters'
    },
    seo_friendly_url_dynamic_check: {
      label: 'Clean URL Structure',
      description: 'URL does not have dynamic parameters'
    },
    seo_friendly_url_keywords_check: {
      label: 'Keywords in URL',
      description: 'URL contains relevant keywords'
    },
    seo_friendly_url_relative_length_check: {
      label: 'URL Length OK',
      description: 'URL length is appropriate'
    },
    has_micromarkup: {
      label: 'Schema Markup Present',
      description: 'Structured data detected'
    },
    from_sitemap: {
      label: 'In Sitemap',
      description: 'Page is included in sitemap'
    }
  };

  Object.entries(checks).forEach(([key, value]) => {
    // Only include checks that are TRUE (indicating a passed check)
    if (value === true && checkDescriptions[key]) {
      passedChecks.push({
        key,
        ...checkDescriptions[key]
      });
    }
  });

  return passedChecks;
};

export const getScoreStatus = (score) => {
  if (score >= 80) return 'good';
  if (score >= 50) return 'warning';
  return 'critical';
};

export const transformResults = (data) => {
  const isPreCalculated = data.scores && data.issues && !data.pages;

  if (isPreCalculated) {
    // old format
  }

  const { scores, issues, psi, crawlInfo, target, taskId, pages, ...rawData } =
    data;

  let overallScore = scores?.overall ?? 0;
  if (!overallScore && pages && pages.length > 0) {
    const totalScore = pages.reduce(
      (acc, page) => acc + (page.onpageScore || 0),
      0
    );
    overallScore = Math.round(totalScore / pages.length);
  }

  const aggregatedIssues = {}; // key -> { count, ...issueData, pages: [] }
  let criticalCount = 0;
  let warningCount = 0;
  let passedCount = 0;

  const checkDescriptions = {
    no_content_encoding: {
      label: 'No Content Encoding',
      severity: 'warning',
      description: 'Page content is not compressed',
      fix: 'Enable GZIP or Brotli compression on your server.'
    },
    high_loading_time: {
      label: 'High Loading Time',
      severity: 'warning',
      description: 'Page takes too long to load',
      fix: 'Optimize images, minimize CSS/JS, and improve server response time.'
    },
    is_redirect: {
      label: 'Redirect Detected',
      severity: 'info',
      description: 'Page redirects to another URL',
      fix: 'Ensure redirects are necessary and use 301 for permanent moves.'
    },
    is_4xx_code: {
      label: '4xx Error',
      severity: 'critical',
      description: 'Client error status code',
      fix: 'Fix broken links and ensure the page exists.'
    },
    is_5xx_code: {
      label: '5xx Error',
      severity: 'critical',
      description: 'Server error status code',
      fix: 'Check server logs for errors and ensure the backend is running.'
    },
    is_broken: {
      label: 'Broken Page',
      severity: 'critical',
      description: 'Page is not accessible',
      fix: 'Resolve the issue preventing page access (DNS, network, etc.).'
    },
    is_http: {
      label: 'HTTPS Not Enabled',
      severity: 'critical',
      description: 'The site does not have a valid SSL certificate.',
      fix: 'Install an SSL certificate and redirect HTTP to HTTPS.'
    },
    high_waiting_time: {
      label: 'High Waiting Time',
      severity: 'warning',
      description: 'Server response time is high',
      fix: 'Optimize backend database queries and server logic.'
    },
    has_micromarkup_errors: {
      label: 'Schema Markup Errors',
      severity: 'warning',
      description: 'Structured data has errors',
      fix: 'Validate and fix Schema.org markup.'
    },
    no_doctype: {
      label: 'Missing Doctype',
      severity: 'warning',
      description: 'HTML doctype declaration missing',
      fix: 'Add <!DOCTYPE html> to the top of the HTML document.'
    },
    no_encoding_meta_tag: {
      label: 'Missing Encoding Meta Tag',
      severity: 'warning',
      description: 'Character encoding not specified',
      fix: 'Add <meta charset="UTF-8"> to the <head> section.'
    },
    no_h1_tag: {
      label: 'Missing H1 Tag',
      severity: 'warning',
      description: 'No H1 heading found on page',
      fix: 'Ensure every page has exactly one <h1> tag describing the main topic.'
    },
    https_to_http_links: {
      label: 'Mixed Content',
      severity: 'warning',
      description: 'HTTPS page links to HTTP resources',
      fix: 'Update all internal links and resources to use HTTPS.'
    },
    size_greater_than_3mb: {
      label: 'Page Size Too Large',
      severity: 'warning',
      description: 'Page size exceeds 3MB',
      fix: 'Compress images and minify resources to reduce page size.'
    },
    has_meta_refresh_redirect: {
      label: 'Meta Refresh Redirect',
      severity: 'warning',
      description: 'Page uses meta refresh for redirect',
      fix: 'Use server-side 301 redirects instead of meta refresh.'
    },
    has_render_blocking_resources: {
      label: 'Render Blocking Resources',
      severity: 'warning',
      description: 'Resources block page rendering',
      fix: 'Defer non-critical CSS/JS or load them asynchronously.'
    },
    low_content_rate: {
      label: 'Low Content Rate',
      severity: 'info',
      description: 'Low text-to-HTML ratio',
      fix: 'Add more unique, high-quality content to the page.'
    },
    high_content_rate: {
      label: 'High Content Rate',
      severity: 'info',
      description: 'Very high text-to-HTML ratio',
      fix: 'Ensure the page structure is utilizing proper HTML tags.'
    },
    low_character_count: {
      label: 'Low Character Count',
      severity: 'info',
      description: 'Page has very few characters',
      fix: 'Expand the content to provide more value to users.'
    },
    high_character_count: {
      label: 'High Character Count',
      severity: 'info',
      description: 'Page has many characters',
      fix: 'Ensure long content is well-structured with headings.'
    },
    low_readability_rate: {
      label: 'Low Readability',
      severity: 'info',
      description: 'Content is difficult to read',
      fix: 'Use simpler sentences, shorter paragraphs, and clear headings.'
    },
    irrelevant_description: {
      label: 'Irrelevant Description',
      severity: 'warning',
      description: 'Meta description may not match content',
      fix: 'Rewrite meta description to accurately summarize page content.'
    },
    irrelevant_title: {
      label: 'Irrelevant Title',
      severity: 'warning',
      description: 'Page title may not match content',
      fix: 'Update page title to reflect the main topic.'
    },
    irrelevant_meta_keywords: {
      label: 'Irrelevant Meta Keywords',
      severity: 'info',
      description: 'Meta keywords may not match content',
      fix: 'Update or remove meta keywords (modern SEO largely ignores them).'
    },
    title_too_long: {
      label: 'Title Too Long',
      severity: 'warning',
      description: 'Page title exceeds recommended length',
      fix: 'Shorten page title to under 60 characters.'
    },
    title_too_short: {
      label: 'Title Too Short',
      severity: 'warning',
      description: 'Page title is too short',
      fix: 'Make page title more descriptive (at least 10 characters).'
    },
    deprecated_html_tags: {
      label: 'Deprecated HTML Tags',
      severity: 'warning',
      description: 'Page uses deprecated HTML elements',
      fix: 'Replace deprecated tags (like <center>, <font>) with CSS.'
    },
    duplicate_meta_tags: {
      label: 'Duplicate Meta Tags',
      severity: 'warning',
      description: 'Multiple identical meta tags found',
      fix: 'Remove duplicate meta tags from the <head>.'
    },
    duplicate_title_tag: {
      label: 'Duplicate Title',
      severity: 'warning',
      description: 'Same title used on multiple pages',
      fix: 'Ensure every page has a unique title tag.'
    },
    no_image_alt: {
      label: 'Missing Image Alt Text',
      severity: 'warning',
      description: 'Images without alt attributes found',
      fix: 'Add descriptive alt text to all <img> tags.'
    },
    no_image_title: {
      label: 'Missing Image Titles',
      severity: 'info',
      description: 'Images without title attributes found',
      fix: 'Add title attributes to images where appropriate.'
    },
    no_description: {
      label: 'Missing Meta Description',
      severity: 'warning',
      description: 'No meta description found',
      fix: 'Add a unique meta description to the page.'
    },
    no_title: {
      label: 'Missing Page Title',
      severity: 'critical',
      description: 'No page title found',
      fix: 'Add a <title> tag to the <head> section.'
    },
    no_favicon: {
      label: 'Missing Favicon',
      severity: 'info',
      description: 'No favicon detected',
      fix: 'Add a favicon link tag to the <head>.'
    },
    flash: {
      label: 'Flash Content',
      severity: 'warning',
      description: 'Page uses Flash (deprecated)',
      fix: 'Remove Flash content and use HTML5/CSS3/JS equivalents.'
    },
    frame: {
      label: 'Frame/IFrame Content',
      severity: 'info',
      description: 'Page uses frames',
      fix: 'Avoid using frames; use CSS or IFrames sparingly.'
    },
    lorem_ipsum: {
      label: 'Lorem Ipsum Detected',
      severity: 'warning',
      description: 'Placeholder text found on page',
      fix: 'Replace placeholder text with actual content.'
    },
    seo_friendly_url: {
      label: 'Non-SEO Friendly URL',
      severity: 'info',
      description: 'URL structure could be improved',
      fix: 'Use clean, descriptive URLs with hyphens.'
    },
    recursive_canonical: {
      label: 'Recursive Canonical',
      severity: 'warning',
      description: 'Canonical URL points to itself incorrectly',
      fix: 'Check canonical tag configuration.'
    },
    canonical_chain: {
      label: 'Canonical Chain',
      severity: 'warning',
      description: 'Multiple canonical redirects detected',
      fix: 'Ensure canonical tag points directly to the final version.'
    },
    canonical_to_redirect: {
      label: 'Canonical to Redirect',
      severity: 'warning',
      description: 'Canonical URL redirects',
      fix: 'Update canonical tag to point to the resolved URL.'
    },
    canonical_to_broken: {
      label: 'Canonical to Broken Page',
      severity: 'critical',
      description: 'Canonical URL points to broken page',
      fix: 'Fix the broken link in the canonical tag.'
    },
    has_links_to_redirects: {
      label: 'Links to Redirects',
      severity: 'info',
      description: 'Page contains links that redirect',
      fix: 'Update internal links to point directly to the final URL.'
    },
    is_orphan_page: {
      label: 'Orphan Page',
      severity: 'warning',
      description: 'Page has no internal links pointing to it',
      fix: 'Add links to this page from other pages on your site.'
    },
    redirect_chain: {
      label: 'Redirect Chain',
      severity: 'warning',
      description: 'Multiple redirects detected',
      fix: 'Reduce redirect chains to a single redirect.'
    }
  };

  if (pages && pages.length > 0) {
    pages.forEach((page) => {
      if (page.checks) {
        Object.entries(page.checks).forEach(([key, value]) => {
          const desc = checkDescriptions[key];

          if (value === true && desc) {
            if (!aggregatedIssues[key]) {
              aggregatedIssues[key] = {
                ...desc,
                key,
                count: 0,
                pages: []
              };
            }
            aggregatedIssues[key].count += 1;
            aggregatedIssues[key].pages.push(page.url);
          }
        });
      }
    });

    Object.values(aggregatedIssues).forEach((issue) => {
      if (issue.severity === 'critical' || issue.severity === 'high') {
        criticalCount += issue.count;
      } else if (issue.severity === 'medium' || issue.severity === 'warning') {
        warningCount += 1;
      }
    });
  }

  let globalRecommendations = [];
  if (Array.isArray(issues)) {
    globalRecommendations = issues.map((issue) => ({
      priority: issue.severity === 'high' ? 'high' : issue.severity,
      title: issue.title,
      description: issue.description,
      fix: issue.howToFix,
      count: issue.count || 1,
      category: issue.category,
      items: issue.items
    }));
  }

  const pageRecommendations = Object.values(aggregatedIssues).map((issue) => ({
    priority: issue.severity === 'high' ? 'high' : issue.severity,
    title: issue.label,
    description: issue.description,
    fix: issue.fix,
    count: issue.count,
    category: 'technical',
    items: issue.pages
      ? issue.pages.map((url) => ({ type: 'page', label: url, url }))
      : []
  }));

  const recommendations = [...globalRecommendations, ...pageRecommendations];

  criticalCount = recommendations.filter(
    (i) => i.priority === 'critical' || i.priority === 'high'
  ).length;
  warningCount = recommendations.filter(
    (i) => i.priority === 'warning' || i.priority === 'medium'
  ).length;

  const categories = [
    {
      name: 'Technical',
      score: scores?.technical ?? overallScore,
      status: getScoreStatus(scores?.technical ?? overallScore)
    },
    {
      name: 'On-Page SEO',
      score: scores?.onPage ?? overallScore,
      status: getScoreStatus(scores?.onPage ?? overallScore)
    },
    {
      name: 'Performance',
      score: scores?.performance ?? overallScore,
      status: getScoreStatus(scores?.performance ?? overallScore)
    },
    {
      name: 'Security',
      score: scores?.security ?? (data.sslInfo ? 100 : overallScore),
      status: getScoreStatus(scores?.security ?? overallScore)
    },
    {
      name: 'Accessibility',
      score: scores?.accessibility ?? 0,
      status: getScoreStatus(scores?.accessibility ?? 0)
    }
  ];

  passedCount = categories.filter((c) => c.score >= 80).length;

  const normalizePsiScores = (psiData) => {
    if (!psiData || !psiData.scores) return null;
    const normalized = { ...psiData };
    const needsNormalization = Object.values(psiData.scores).some(
      (val) => val > 1
    );
    if (needsNormalization) {
      normalized.scores = { ...psiData.scores };
      Object.keys(normalized.scores).forEach((key) => {
        let val = parseFloat(normalized.scores[key]);
        if (val > 1) val = val / 100;
        normalized.scores[key] = val;
      });
    }
    normalized.lighthouseCategories = normalized.scores;
    return normalized;
  };

  let psiMobile = null;
  let psiDesktop = null;
  if (psi) {
    if (psi.mobile || psi.desktop) {
      psiMobile = normalizePsiScores(psi.mobile);
      psiDesktop = normalizePsiScores(psi.desktop);
    } else if (psi.scores) {
      psiMobile = normalizePsiScores(psi);
    }
  }

  const issuesTitles = (issues || []).map((i) => i.title);
  const hasSsl = data.sslInfo || !issuesTitles.includes('HTTPS Not Enabled');
  const pageChecksAgg = {};
  if (pages && pages.length > 0) {
    pages.forEach((p) => {
      if (p.checks) {
        Object.entries(p.checks).forEach(([k, v]) => {
          if (v === true) pageChecksAgg[k] = (pageChecksAgg[k] || 0) + 1;
        });
      }
    });
  }

  const healthChecks = [
    {
      key: 'https',
      label: 'HTTPS & SSL',
      passed: !!hasSsl,
      why: 'HTTPS encrypts user data and is a confirmed Google ranking signal.'
    },
    {
      key: 'redirects',
      label: 'HTTP Redirects',
      passed:
        !issuesTitles.includes('Redirect Loops') &&
        !(pageChecksAgg.redirect_chain > 0),
      why: 'Clean redirects prevent crawl budget waste and preserve link equity.'
    },
    {
      key: 'canonical',
      label: 'Canonical Tag',
      passed: !(
        pageChecksAgg.recursive_canonical > 0 ||
        pageChecksAgg.canonical_chain > 0 ||
        pageChecksAgg.canonical_to_broken > 0
      ),
      why: 'Canonical tags prevent duplicate content issues and consolidate ranking signals.'
    },
    {
      key: 'robots',
      label: 'Robots.txt',
      passed: !issuesTitles.includes('Missing robots.txt'),
      why: 'Robots.txt guides search engine crawlers and protects private areas of your site.'
    },
    {
      key: 'sitemap',
      label: 'Sitemap.xml',
      passed: !issuesTitles.includes('Missing Sitemap'),
      why: 'A sitemap helps search engines discover and index all your important pages faster.'
    },
    {
      key: 'indexability',
      label: 'Indexability',
      passed: !issuesTitles.includes('Non-Indexable Pages'),
      why: 'Pages must be indexable for search engines to show them in results.'
    },
    {
      key: 'broken_links',
      label: 'Broken Internal Links',
      passed: !issuesTitles.includes('Broken Internal Links'),
      why: 'Broken links hurt user experience and waste crawl budget on dead-end pages.'
    },
    {
      key: 'title',
      label: 'Title Tag',
      passed:
        !issuesTitles.includes('Missing Page Title') &&
        !issuesTitles.includes('Title Too Long') &&
        !issuesTitles.includes('Title Too Short'),
      why: 'Title tags are the most important on-page ranking factor and your first impression in search results.'
    },
    {
      key: 'description',
      label: 'Meta Description',
      passed: !issuesTitles.includes('Missing Meta Description'),
      why: 'Meta descriptions influence click-through rates from search engine results pages.'
    },
    {
      key: 'h1',
      label: 'H1 Heading',
      passed:
        !issuesTitles.includes('Missing H1 Tag') &&
        !(pageChecksAgg.no_h1_tag > 0),
      why: 'A single, clear H1 heading helps search engines understand the primary topic of each page.'
    },
    {
      key: 'img_alt',
      label: 'Image Alt Text',
      passed:
        !issuesTitles.includes('Missing Image Alt Text') &&
        !(pageChecksAgg.no_image_alt > 0),
      why: 'Alt text makes images accessible to screen readers and helps Google Image Search rank your images.'
    }
  ];

  return {
    url: target || (pages && pages[0]?.url) || '',
    score: overallScore,
    issues: {
      critical: criticalCount,
      warnings: warningCount,
      passed: passedCount
    },
    categories,
    recommendations,
    healthChecks,
    psi: { mobile: psiMobile, desktop: psiDesktop },
    crawlInfo: crawlInfo || (pages ? { pagesCrawled: pages.length } : null),
    taskId,
    rawData: rawData || {},
    rawScores: scores,
    rawIssues: issues,
    reportId: data.reportId || null,
    pages: pages || []
  };
};
